import { useDebounceFn } from '@vueuse/core'

export function useOverflowDetection(
	slotElement: Ref<HTMLDivElement | null>,
	options: { observe?: Ref<boolean>; root?: Ref<HTMLDivElement | null> }
) {
	const isOverflowing = ref(false)
	const showScrollLeft = ref(false)
	const showScrollRight = ref(false)
	const wrapperRef = computed(() => {
		if (options?.root) {
			return options.root.value
		}
		return null
	})
	const contentRef = computed(() => {
		if (slotElement) {
			return slotElement.value
		}
		return null
	})
	const checkOverflow = useDebounceFn(() => {
		if (wrapperRef.value && contentRef.value) {
			const isOverflowingHorizontally = contentRef.value.scrollWidth > wrapperRef.value.clientWidth
			isOverflowing.value = isOverflowingHorizontally

			showScrollLeft.value = contentRef.value.scrollLeft > 0
			showScrollRight.value =
				contentRef.value.scrollLeft < contentRef.value.scrollWidth - contentRef.value.clientWidth
		}
	}, 1000)

	const scrollElement = () => {
		if (wrapperRef.value && contentRef.value) {
			contentRef.value.scrollLeft = contentRef.value.scrollLeft < 1 ? contentRef.value.scrollWidth : 0
			checkOverflow()
		}
	}

	watchEffect(() => {
		checkOverflow()
	})

	watch(
		contentRef,
		(newEl, oldEl) => {
			if (oldEl) {
				oldEl.removeEventListener('scroll', checkOverflow)
			}
			if (newEl) {
				newEl.addEventListener('scroll', checkOverflow)
				checkOverflow()
			}
		},
		{ immediate: true }
	)

	onMounted(() => {
		window.addEventListener('resize', checkOverflow)
	})

	onUnmounted(() => {
		if (contentRef.value) {
			contentRef.value.removeEventListener('scroll', checkOverflow)
		}
		window.removeEventListener('resize', checkOverflow)
	})

	if (options?.observe) {
		watch(
			options.observe,
			e => {
				checkOverflow()
				if (slotElement.value && !e) {
					checkOverflow()
				}
			},
			{ immediate: true }
		)
	}
	return {
		isOverflowing,
		showScrollLeft,
		showScrollRight,
		checkOverflow,
		scrollElement
	}
}
